import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { timeLogAPI, jobCodeAPI } from "../../services/api";
import { useAuth } from "../AuthContext";
import StaffLayout from "./StaffLayout";
import ShiftStatusBanner from "./ShiftStatusBanner";
import MissingPunchSection from "./MissingPunchSection";
import AutoCloseBanner from "./AutoCloseBanner";
import EndBreakClockOutSheet from "./EndBreakClockOutSheet";
import ClockInJobCodeSheet from "./ClockInJobCodeSheet";
import ClockOutJobCodeSheet from "./ClockOutJobCodeSheet";
import {
  acquireLocation,
  geoErrorCopy,
  GEO_ERRORS,
} from "../../utils/geoUtils";
import { formatStudioTime } from "../../utils/workforceTimeUtils";
import "./ClockInOutScreen.css";

/**
 * Workforce — Clock In / Clock Out screen (TIME-004a slice).
 *
 * Authoritative inputs:
 *   - docs/architecture/TIME-004a-clock-in-out-tech.md (endpoint contracts §6,
 *     React Query keys §7.1, geolocation pattern §7.3, error copy §9)
 *   - docs/design/TIME-004-mobile-clock-in-design.md (TIME-004a checklist)
 *   - docs/design/TIME-005-design.md (Integration §"Region 2" / "Region 3"
 *     swap targets — break button + clock-out label flip; mounted in a later
 *     story but the slot is reserved here so the swap is a prop change rather
 *     than a layout rewrite)
 *
 * TIME-005 (2026-04-28):
 *   - Region 2 break button (Start Break / End Break) mounted below.
 *   - Region 3 Clock Out button label flips to "End break and clock out"
 *     when the parent state is ON_BREAK; that CTA opens a confirmation
 *     bottom-sheet whose Confirm calls /clock-out (the server atomically
 *     ends any open break — single-mutation contract per the design spec
 *     §"Cross-stack note for Marcus").
 *
 * Out of scope per TIME-004a + TIME-005:
 *   - Wi-Fi IP allowlist fallback (TIME-004b)
 *   - Offline queue / IndexedDB caching (TIME-004b)
 *   - Manager override (TIME-012)
 *   - Paid/unpaid affordance — admin-only surface (TIME-009 / PAY-005)
 *   - Manager-side break adjustment (TIME-010)
 *
 * Polling cadence per tech doc §6.5: every 30s while page is foregrounded;
 * pause on visibilitychange.
 *
 * RBAC: any authenticated staff role (staff, admin, massage_therapist,
 * luminary). The route-level ProtectedRoute in App.js gates entry; this
 * component does not re-check role since the AuthContext shape already
 * normalized that surface for this app's other staff screens.
 */
const POLL_INTERVAL_MS = 30_000;

/**
 * Stable shape we render from. Combines the server's open-shift response
 * with a "loading" / "idle" sentinel so the JSX only branches on `mode`.
 */
const STATUS = {
  LOADING: "loading",
  IDLE: "idle",        // not clocked in
  CLOCKED_IN: "clocked-in",
  ON_BREAK: "on-break",
};

const ClockInOutScreen = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(STATUS.LOADING);
  const [openTimeLog, setOpenTimeLog] = useState(null);
  // submitting is shared across clock-in / clock-out / break submit so the
  // entire CTA region locks during any in-flight mutation per spec
  // (§"Race: staff taps End Break and End-break-and-clock-out
  // near-simultaneously" — both buttons enter loading state on first tap).
  const [submitting, setSubmitting] = useState(false);
  const [pollError, setPollError] = useState(false);
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false);
  // TIME-006 — job-code picker sheet at clock-in.
  const [jobCodes, setJobCodes] = useState([]);
  const [jobCodeSheetOpen, setJobCodeSheetOpen] = useState(false);
  const [pendingClockInGeo, setPendingClockInGeo] = useState(null);
  // TIME-006-V2 — clock-out confirm/change sheet state. Opens when staff
  // taps Clock Out from the CLOCKED_IN state AND the open TimeLog has a
  // jobCode set. The skip rule per design spec §5: skip the sheet only when
  // the row's `jobCode === null`. If the row has a value (active or stale),
  // the sheet opens.
  const [clockOutSheetOpen, setClockOutSheetOpen] = useState(false);
  const [clockOutSheetError, setClockOutSheetError] = useState(null);
  const intervalRef = useRef(null);

  /**
   * Fetch open-shift state from the server and update local mode.
   * Per tech doc §7.1, this is the source of truth — we never optimistically
   * advance to CLOCKED_IN before the server confirms (§7.2 — geo-fence
   * rejection is too common in the parking lot for that to be acceptable UX).
   */
  const refreshOpenShift = useCallback(async () => {
    try {
      const response = await timeLogAPI.getOpenShift();
      const open = response?.data?.openTimeLog || null;
      setOpenTimeLog(open);
      if (!open) {
        setStatus(STATUS.IDLE);
      } else if (open.currentBreak && open.currentBreak.startTime) {
        // TIME-005 swap target — supported here for forward compatibility but
        // the break-start UI is not built in TIME-004a.
        setStatus(STATUS.ON_BREAK);
      } else {
        setStatus(STATUS.CLOCKED_IN);
      }
      setPollError(false);
    } catch (err) {
      // Don't toast on every poll failure — that would spam. Surface a quiet
      // banner only after the FIRST load fails; subsequent failures are
      // tolerated silently per the design's "informational, not blocking"
      // posture for the persistent banner.
      if (status === STATUS.LOADING) {
        setStatus(STATUS.IDLE);
      }
      setPollError(true);
    }
  }, [status]);

  // Mount: fetch immediately, then start the 30s poll. Pause on visibility
  // change (tech doc §6.5).
  useEffect(() => {
    let cancelled = false;

    const startPolling = () => {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        if (!cancelled) refreshOpenShift();
      }, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        // immediate refetch on resume + restart poll
        refreshOpenShift();
        startPolling();
      } else {
        stopPolling();
      }
    };

    refreshOpenShift();
    startPolling();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    return () => {
      cancelled = true;
      stopPolling();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
    // refreshOpenShift's deps are stable enough — we intentionally do not
    // re-bind the interval on every status flip (the body of the callback
    // re-reads state from setState dispatch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TIME-006 §3.3 — fetch the active job-code list once on mount. Cached for
  // the lifetime of the page; per-tap re-fetching is wasteful and the list
  // is admin-managed (low write rate). On failure: empty list -> sheet skips.
  useEffect(() => {
    let cancelled = false;
    jobCodeAPI
      .list({ active: true })
      .then((res) => {
        if (cancelled) return;
        const list = res?.data?.jobCodes || res?.data || [];
        setJobCodes(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (cancelled) return;
        setJobCodes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Map a tech-doc error code to user-facing copy. Mirrors §9 of the tech
   * doc and the AC §"Scenario 2" / §"Scenario 4" copy.
   */
  const errorCopyForResponse = (errCode, details) => {
    switch (errCode) {
      case "geofence_rejection": {
        const meters = details?.distanceMeters;
        const distancePart =
          typeof meters === "number" && Number.isFinite(meters)
            ? ` (You appear to be ${Math.round(meters)}m away.)`
            : "";
        return `You must be at the studio to clock in.${distancePart}`;
      }
      case "geofence_rejection_clock_out": {
        const meters = details?.distanceMeters;
        const distancePart =
          typeof meters === "number" && Number.isFinite(meters)
            ? ` (You appear to be ${Math.round(meters)}m away.)`
            : "";
        return `You must be at the studio to clock out.${distancePart} Contact your manager if this is wrong.`;
      }
      case "already_clocked_in": {
        const since = details?.punchInTime
          ? formatStudioTime(details.punchInTime)
          : "";
        return since
          ? `You're already clocked in (since ${since}). Refreshing your status...`
          : "You're already clocked in. Refreshing your status...";
      }
      case "no_open_shift":
        // The "you tried to start a break but you're not clocked in" toast.
        // TIME-005 design §"Error toast" Scenario 2 copy. The defensive
        // catch-all kept generic — the break button is hidden when the user
        // is not clocked-in, so this is reachable only via stale state /
        // direct API. The Scenario-2 inline "Clock In" link is downgraded
        // to the page CTA per the spec's escape hatch.
        return "You're not clocked in. Clock in first?";
      case "already_on_break":
        return "You're already on break. End your current break first.";
      case "not_on_break":
        return "You're not currently on break.";
      case "studio_geo_not_configured":
        return "Time-tracking is not configured for this studio. Please contact your manager.";
      case "invalid_job_code":
        return "That role isn't recognized. Please pick a different role.";
      case "staff_profile_not_found":
        return "We could not find a staff profile for your account. Please contact your manager.";
      case "missing_coordinates":
        return "Location data did not reach the server. Please try again.";
      default:
        return "Couldn't reach the server. Please try again.";
    }
  };

  /**
   * Submit the clock-in mutation with the captured geo + optional jobCode.
   * Per tech doc §7.2 we do NOT optimistically render — the banner appears
   * only after the server confirms.
   */
  const performClockInSubmit = useCallback(
    async (geo, jobCode) => {
      try {
        const payload = {
          coordinates: geo.coordinates,
          accuracy: geo.accuracy,
          clientType: "web",
        };
        // Only include jobCode when the user picked one. Omitting the field
        // preserves the server's primaryRole-fallback resolution chain
        // (TIME-006-tech.md §5.1).
        if (jobCode) payload.jobCode = jobCode;
        await timeLogAPI.clockIn(payload);
        toast.success("Clocked in.");
        await refreshOpenShift();
      } catch (err) {
        const code = err?.response?.data?.error;
        const data = err?.response?.data || {};
        const msg = errorCopyForResponse(code, data);
        toast.error(msg);
        if (code === "already_clocked_in") {
          await refreshOpenShift();
        }
      } finally {
        setSubmitting(false);
        setPendingClockInGeo(null);
      }
    },
    [refreshOpenShift]
  );

  /**
   * Acquire location, then either open the job-code sheet (TIME-006 §3.3 —
   * when the org has at least one active code) or submit directly.
   */
  const handleClockIn = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    let geo;
    try {
      geo = await acquireLocation();
    } catch (geoErr) {
      const msg = geoErrorCopy(geoErr.code || GEO_ERRORS.UNKNOWN);
      toast.error(msg);
      setSubmitting(false);
      return;
    }

    // AC Scenario 3 — zero codes configured: skip the sheet entirely and
    // proceed with the legacy single-tap flow.
    if (!jobCodes || jobCodes.length === 0) {
      await performClockInSubmit(geo, null);
      return;
    }

    // Otherwise open the picker sheet. The submitting flag stays true so the
    // primary CTA cannot be re-tapped while the sheet is open. The sheet's
    // Confirm calls performClockInSubmit; Cancel resets state without firing.
    setPendingClockInGeo(geo);
    setJobCodeSheetOpen(true);
  }, [submitting, jobCodes, performClockInSubmit]);

  const handleJobCodeSheetConfirm = useCallback(
    async (jobCode) => {
      const geo = pendingClockInGeo;
      setJobCodeSheetOpen(false);
      if (!geo) {
        // Defensive — pendingClockInGeo should always be set when the sheet
        // is open. If it isn't, just clear the flag.
        setSubmitting(false);
        return;
      }
      await performClockInSubmit(geo, jobCode || null);
    },
    [pendingClockInGeo, performClockInSubmit]
  );

  const handleJobCodeSheetCancel = useCallback(() => {
    setJobCodeSheetOpen(false);
    setPendingClockInGeo(null);
    setSubmitting(false);
  }, []);

  /**
   * Acquire location, then POST clock-out. On success, refresh — the banner
   * disappears when openTimeLog is null.
   *
   * TIME-005: when called from the on-break confirmation sheet's Confirm,
   * the server atomically ends the open break alongside the punch-out. No
   * separate /breaks/end call is needed — single mutation, single spinner.
   * The sheet closes on both success and failure (failure surfaces the
   * toast on the parent page; state is unchanged so the user can retry).
   *
   * TIME-006-V2: optional `submittedJobCode` arg. When the v2 confirm-or-
   * change sheet is the entry point, the sheet's Confirm handler calls
   * here with the picked code. We forward it as the optional `jobCode`
   * body field per Marcus tech doc §3.1. When omitted (legacy paths —
   * end-break-and-clock-out, idle-state Clock Out from a row with no
   * punch-in code), we send the legacy payload shape and the backend
   * takes the no-mutation branch on the jobCode side.
   *
   * On a 400 invalid_job_code response (TIME-006-V2 §3.1), we keep the
   * v2 sheet OPEN with the picker in error state so staff can retry —
   * design spec §States Summary "Error" + §AC Scenario 6. All other
   * failure modes close the sheet and surface the toast.
   */
  const handleClockOut = useCallback(
    async (submittedJobCode) => {
      if (submitting) return;
      setSubmitting(true);
      let geo;
      try {
        geo = await acquireLocation();
      } catch (geoErr) {
        const msg = geoErrorCopy(geoErr.code || GEO_ERRORS.UNKNOWN);
        toast.error(msg);
        setSubmitting(false);
        // Close the sheets on geo failure — keeps the user on a single,
        // visible CTA to retry. Sheet re-opens on next tap.
        setConfirmSheetOpen(false);
        setClockOutSheetOpen(false);
        setClockOutSheetError(null);
        return;
      }
      try {
        const payload = {
          coordinates: geo.coordinates,
          accuracy: geo.accuracy,
          clientType: "web",
        };
        // TIME-006-V2 §3.1 — only include `jobCode` when the v2 sheet
        // emitted a value AND it differs from the open TimeLog's punch-
        // in code. Equal-value omission keeps the backend on the no-op
        // branch (no jobCodeChangedAt set, no audit row) per tech doc
        // §3.1 + AC Scenario 1.
        if (
          typeof submittedJobCode === "string" &&
          submittedJobCode.length > 0 &&
          submittedJobCode !== openTimeLog?.jobCode
        ) {
          payload.jobCode = submittedJobCode;
        }
        await timeLogAPI.clockOut(payload);
        toast.success("Clocked out.");
        setConfirmSheetOpen(false);
        setClockOutSheetOpen(false);
        setClockOutSheetError(null);
        await refreshOpenShift();
      } catch (err) {
        const code = err?.response?.data?.error;
        const data = err?.response?.data || {};
        // TIME-006-V2 §AC Scenario 6 — keep the v2 sheet open with the
        // picker in error state on invalid_job_code so staff can retry
        // without restarting the clock-out flow.
        if (code === "invalid_job_code" && clockOutSheetOpen) {
          setClockOutSheetError(
            "That job code is no longer active. Pick a different one."
          );
          // Do NOT close the sheet; do NOT toast (keep the error scoped
          // to the picker per design spec §States Summary).
          return;
        }
        const msg = errorCopyForResponse(code, data);
        toast.error(msg);
        // Close the sheets on any other failure — parent page surfaces
        // the toast. For no_open_shift we also refresh to re-sync.
        setConfirmSheetOpen(false);
        setClockOutSheetOpen(false);
        setClockOutSheetError(null);
        if (code === "no_open_shift") {
          await refreshOpenShift();
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, refreshOpenShift, openTimeLog, clockOutSheetOpen]
  );

  /**
   * TIME-006-V2 entry point — staff taps the Clock Out CTA from the
   * CLOCKED_IN state. Decide whether to open the v2 confirm/change sheet
   * (the row has a `jobCode`) or to fall through to the legacy direct
   * clock-out path (the row has `jobCode === null` — design spec §5
   * "skip the sheet only when the TimeLog row has `jobCode === null`").
   */
  const handleClockOutCtaTap = useCallback(() => {
    if (submitting) return;
    const punchInCode = openTimeLog?.jobCode;
    if (typeof punchInCode === "string" && punchInCode.length > 0) {
      // Open the v2 sheet; the API call fires when the sheet's Confirm
      // handler calls handleClockOut(picked).
      setClockOutSheetError(null);
      setClockOutSheetOpen(true);
      return;
    }
    // Legacy path — no punch-in code on the row, no v2 sheet to render.
    handleClockOut(undefined);
  }, [submitting, openTimeLog, handleClockOut]);

  const handleClockOutSheetConfirm = useCallback(
    (pickedCode) => {
      // Sheet stays visually mounted during the in-flight mutation so the
      // CTA can show its loading state per design spec §States Summary
      // "Submitting". handleClockOut closes the sheet on success/failure
      // (or keeps it open on invalid_job_code — Scenario 6).
      handleClockOut(pickedCode);
    },
    [handleClockOut]
  );

  const handleClockOutSheetCancel = useCallback(() => {
    if (submitting) return;
    // AC Scenario 4: dismissal aborts the clock-out — no API call fires.
    setClockOutSheetOpen(false);
    setClockOutSheetError(null);
  }, [submitting]);

  /**
   * Start a break. No geolocation required (the geo-fence gate is at
   * clock-in/clock-out only — TIME-005 design §"Cross-stack note for
   * Marcus"). Server resolves the open shift from the JWT and appends a
   * new entry to `breaks[]`. On success, refresh to pick up the
   * `currentBreak` field which flips Region 1 + Region 2 + Region 3.
   */
  const handleStartBreak = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await timeLogAPI.startBreak();
      await refreshOpenShift();
    } catch (err) {
      const code = err?.response?.data?.error;
      const data = err?.response?.data || {};
      const msg = errorCopyForResponse(code, data);
      toast.error(msg);
      // Re-sync on either of the "you're in the wrong state" rejections —
      // that catches the case where the server already advanced state on
      // a different device (TIME-005 design §"Edge cases" #8).
      if (code === "no_open_shift" || code === "already_on_break") {
        await refreshOpenShift();
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, refreshOpenShift]);

  /**
   * End a break (without clocking out). Server sets `breakEnd` on the
   * latest open `breaks[]` entry. UI re-sync flips state back to
   * CLOCKED_IN.
   */
  const handleEndBreak = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await timeLogAPI.endBreak();
      await refreshOpenShift();
    } catch (err) {
      const code = err?.response?.data?.error;
      const data = err?.response?.data || {};
      const msg = errorCopyForResponse(code, data);
      toast.error(msg);
      if (code === "no_open_shift" || code === "not_on_break") {
        await refreshOpenShift();
      }
    } finally {
      setSubmitting(false);
    }
  }, [submitting, refreshOpenShift]);

  /**
   * Open the bottom-sheet confirmation for the combined "End break and
   * clock out" CTA. The sheet itself does NOT fire the API — its Confirm
   * handler routes back through `handleClockOut`.
   */
  const handleOpenEndBreakClockOutSheet = useCallback(() => {
    if (submitting) return;
    setConfirmSheetOpen(true);
  }, [submitting]);

  const handleCancelEndBreakClockOutSheet = useCallback(() => {
    if (submitting) return;
    setConfirmSheetOpen(false);
  }, [submitting]);

  // ----- Render branches -----

  if (status === STATUS.LOADING) {
    return (
      <StaffLayout>
        <div className="clock-screen" data-testid="clock-screen-loading">
          <p className="clock-screen__loading">Checking your status...</p>
        </div>
      </StaffLayout>
    );
  }

  const firstName = user?.firstName || user?.name || "";
  const greeting = firstName ? `Hi, ${firstName}` : "Hi";

  return (
    <StaffLayout>
      <div className="clock-screen" data-testid="clock-screen">
        <header className="clock-screen__header">
          <h1 className="clock-screen__greeting">{greeting}</h1>
        </header>

        {pollError && (
          <p
            className="clock-screen__poll-error"
            role="alert"
            data-testid="clock-screen-poll-error"
          >
            We are having trouble reaching the server. Showing your last known
            status.
          </p>
        )}

        {/* TIME-010 §3.1 — Submit Missing Punch surface (own-list +
            entry-point button). Renders above the clock-in card whether
            currently clocked in, on break, or not clocked in. */}
        <MissingPunchSection />

        {/* TIME-007 §5 — Auto-close banner. Surfaces when the open shift
            has `flagged_overlong: true` (flag_only behavior mode); silent
            otherwise. The auto_close behavior path closes the shift
            entirely so getOpenShift returns null — that path's recovery
            belongs to a future v1.1 banner endpoint per Marcus tech doc
            §5. v1 ships flag_only directly. */}
        <AutoCloseBanner openTimeLog={openTimeLog} />

        {/* Region 1 — status line. Banner only renders when there's an open
            shift; idle state shows the explainer instead. */}
        {(status === STATUS.CLOCKED_IN || status === STATUS.ON_BREAK) &&
          openTimeLog && (
            <ShiftStatusBanner
              mode={status === STATUS.ON_BREAK ? "on-break" : "clocked-in"}
              startedAt={
                status === STATUS.ON_BREAK
                  ? openTimeLog.currentBreak?.startTime
                  : openTimeLog.punchInTime
              }
            />
          )}

        {/* Region 2 — break button. Hidden in IDLE (entire slot empty per
            spec §"Region 2"); Start Break (outlined teal, 56pt) when
            CLOCKED_IN; End Break (gold, 56pt) when ON_BREAK. */}
        <div
          className="clock-screen__break-slot"
          data-testid="clock-screen-break-slot"
          aria-hidden={status === STATUS.IDLE}
        >
          {status === STATUS.CLOCKED_IN && (
            <button
              type="button"
              className="clock-screen__cta clock-screen__cta--secondary"
              onClick={handleStartBreak}
              disabled={submitting}
              data-testid="start-break-button"
              aria-label="Start Break"
            >
              {submitting ? "Starting break..." : "Start Break"}
            </button>
          )}

          {status === STATUS.ON_BREAK && (
            <button
              type="button"
              className="clock-screen__cta clock-screen__cta--break-primary"
              onClick={handleEndBreak}
              disabled={submitting}
              data-testid="end-break-button"
              aria-label="End Break"
            >
              {submitting ? "Ending break..." : "End Break"}
            </button>
          )}
        </div>

        {/* Region 3 — primary CTA.
              IDLE        -> Clock In (gold, 64pt — TIME-004a default)
              CLOCKED_IN  -> Clock Out (gold, 64pt — TIME-004a default)
              ON_BREAK    -> "End break and clock out" (outlined teal, 56pt;
                            opens confirmation sheet — does NOT fire the
                            mutation directly per TIME-005 spec) */}
        <div className="clock-screen__cta-region">
          {status === STATUS.IDLE && (
            <button
              type="button"
              className="clock-screen__cta clock-screen__cta--primary"
              onClick={handleClockIn}
              disabled={submitting}
              data-testid="clock-in-button"
              aria-label="Clock In"
            >
              {submitting ? "Clocking in..." : "Clock In"}
            </button>
          )}

          {status === STATUS.CLOCKED_IN && (
            <button
              type="button"
              className="clock-screen__cta clock-screen__cta--primary"
              onClick={handleClockOutCtaTap}
              disabled={submitting}
              data-testid="clock-out-button"
              aria-label="Clock Out"
            >
              {submitting ? "Clocking out..." : "Clock Out"}
            </button>
          )}

          {status === STATUS.ON_BREAK && (
            <button
              type="button"
              className="clock-screen__cta clock-screen__cta--secondary"
              onClick={handleOpenEndBreakClockOutSheet}
              disabled={submitting}
              data-testid="clock-out-button-on-break"
              aria-label="End break and clock out"
            >
              End break and clock out
            </button>
          )}
        </div>

        <p className="clock-screen__footnote">
          Times shown in MST. Location is captured only at the moment you tap
          Clock In or Clock Out.
        </p>

        <EndBreakClockOutSheet
          open={status === STATUS.ON_BREAK && confirmSheetOpen}
          breakStartedAt={openTimeLog?.currentBreak?.startTime}
          shiftStartedAt={openTimeLog?.punchInTime}
          totalBreakMinutesSoFar={openTimeLog?.totalBreakMinutesSoFar || 0}
          submitting={submitting}
          onConfirm={handleClockOut}
          onCancel={handleCancelEndBreakClockOutSheet}
        />

        {/* While the sheet is open, the API call has NOT fired yet (the
            user is still picking). The sheet's Confirm button is interactive;
            spinner appears only after Confirm closes the sheet and the
            performClockInSubmit mutation is in flight (sheet is closed by
            then, so this prop is effectively for the brief overlap before
            the close re-renders). */}
        <ClockInJobCodeSheet
          open={jobCodeSheetOpen}
          codes={jobCodes}
          submitting={false}
          onConfirm={handleJobCodeSheetConfirm}
          onCancel={handleJobCodeSheetCancel}
        />

        {/* TIME-006-V2 §3 — clock-out confirm/change sheet. Renders only
            when the user has tapped Clock Out from CLOCKED_IN AND the
            open TimeLog has a jobCode set. The sheet stays mounted
            during the in-flight mutation so the primary CTA can show
            its loading state (design spec §States Summary "Submitting"),
            and stays open on invalid_job_code (§AC Scenario 6) with
            the picker in error state. */}
        <ClockOutJobCodeSheet
          open={clockOutSheetOpen}
          codes={jobCodes}
          punchInJobCode={openTimeLog?.jobCode || null}
          punchInDisplayName={
            (jobCodes || []).find(
              (c) => c.code === openTimeLog?.jobCode
            )?.displayName || null
          }
          submitting={submitting}
          errorMessage={clockOutSheetError}
          onConfirm={handleClockOutSheetConfirm}
          onCancel={handleClockOutSheetCancel}
        />
      </div>
    </StaffLayout>
  );
};

export default ClockInOutScreen;
